import { Dialog as DialogPrimitive } from '@base-ui/react/dialog';
import { IconButton } from '@plato/ui/candidates/icon-button';
import { Container } from '@plato/ui/containers/container';
import { XIcon } from 'lucide-react';
import type { ComponentPropsWithRef, ReactElement } from 'react';
import { cn } from '../../../lib/utils.ts';

type RootPrimitiveProps = Omit<DialogPrimitive.Root.Props, 'onOpenChange'> & {
    onOpenChange?: (open: boolean) => void;
};

type PopupPrimitiveProps = Omit<DialogPrimitive.Popup.Props, 'className' | 'render'> & {
    className?: string;
};

/** Matches the `w-96` / `pr-96` the panel and the layout are sized with. */
const sidePanelWidth = 384;

/**
 * The app's own breakpoints are viewport-based, so they never fire for a body
 * the panel narrowed — 1030px is the narrowest body the app is designed to
 * render at (the `xl` viewport minus the expanded sidebar).
 */
const minAppContentWidth = 1030;

const expandedSidebarWidth = 250;

/** Below this the panel floats over the content instead of docking beside it. */
export const minDockedViewportWidth = expandedSidebarWidth + minAppContentWidth + sidePanelWidth;

export type SidePanelProps = Omit<RootPrimitiveProps, 'modal'>;

/**
 * A companion surface that stays open while the rest of the app keeps working.
 * It has three appearances, driven by how much room the viewport has:
 *
 * - below `sm` — full screen, closed from the header
 * - narrower viewports — a sheet floating over the content
 * - wide enough for both minimum widths — docked side by side with the app
 *
 * The floating sheet is the wider of the two: it covers the content rather than
 * squeezing it, so nothing is gained by keeping it as narrow as the docked
 * width, which the layout has to give up to reserve.
 *
 * Built on the plain dialog primitive rather than the drawer one: there is no
 * swipe-to-dismiss and no enter/exit animation, so the drawer's gesture and
 * snap-point machinery would only be dead weight.
 *
 * Deliberately non-modal — no focus trap, no scroll lock, and outside presses
 * don't dismiss it — so a user can scroll a list or open a customer while the
 * panel stays put.
 */
export function SidePanel({ children, onOpenChange, ...props }: SidePanelProps): ReactElement {
    return (
        <DialogPrimitive.Root
            {...props}
            modal={false}
            disablePointerDismissal
            onOpenChange={(open) => onOpenChange?.(open)}
        >
            {children}
        </DialogPrimitive.Root>
    );
}

export type SidePanelTriggerProps = DialogPrimitive.Trigger.Props;

export function SidePanelTrigger(props: SidePanelTriggerProps): ReactElement {
    return <DialogPrimitive.Trigger data-slot="side-panel-trigger" {...props} />;
}

export type SidePanelContentProps = PopupPrimitiveProps & {
    docked?: boolean;
};

export function SidePanelContent({
    className,
    children,
    docked = false,
    ...props
}: SidePanelContentProps): ReactElement {
    return (
        <DialogPrimitive.Portal>
            <DialogPrimitive.Viewport
                // `pointer-events-none` on the viewport is what keeps the app
                // underneath clickable while the panel is open
                className="pointer-events-none fixed inset-0 z-50 flex justify-end"
            >
                <Container
                    level={1}
                    render={
                        <DialogPrimitive.Popup
                            data-slot="side-panel-content"
                            className={cn(
                                'pointer-events-auto relative flex h-full w-full min-h-0 flex-col overflow-hidden',
                                'border-l border-line-weak bg-container text-content outline-none',
                                // With no exit animation the primitive keeps the
                                // popup mounted, so hide it on the closed state.
                                'data-closed:hidden',
                                docked ? 'sm:w-96' : 'sm:w-2xl sm:shadow-2xl sm:shadow-black/10',
                                className,
                            )}
                            {...props}
                        />
                    }
                >
                    {children}
                </Container>
            </DialogPrimitive.Viewport>
        </DialogPrimitive.Portal>
    );
}

export type SidePanelHeaderProps = ComponentPropsWithRef<'div'>;

export function SidePanelHeader({ className, ...props }: SidePanelHeaderProps): ReactElement {
    return (
        <Container
            data-slot="side-panel-header"
            className={cn(
                'flex shrink-0 items-center gap-cozy border-b border-line-weak px-container py-container',
                className,
            )}
            {...props}
        />
    );
}

export type SidePanelTitleProps = Omit<DialogPrimitive.Title.Props, 'className' | 'render'> & {
    className?: string;
};

export function SidePanelTitle({ className, ...props }: SidePanelTitleProps): ReactElement {
    return (
        <DialogPrimitive.Title
            data-slot="side-panel-title"
            className={cn('mr-auto text-sm font-semibold', className)}
            {...props}
        />
    );
}

export type SidePanelCloseProps = {
    label: string;
};

export function SidePanelClose({ label }: SidePanelCloseProps): ReactElement {
    return (
        <DialogPrimitive.Close
            data-slot="side-panel-close"
            render={
                <IconButton label={label} variant="secondary">
                    <XIcon />
                </IconButton>
            }
        />
    );
}

export type SidePanelBodyProps = ComponentPropsWithRef<'div'>;

export function SidePanelBody({ className, ...props }: SidePanelBodyProps): ReactElement {
    return (
        <div
            data-slot="side-panel-body"
            className={cn(
                'min-h-0 flex-1 overflow-auto overscroll-contain px-container py-container',
                className,
            )}
            {...props}
        />
    );
}

export type SidePanelFooterProps = ComponentPropsWithRef<'div'>;

export function SidePanelFooter({ className, ...props }: SidePanelFooterProps): ReactElement {
    return (
        <Container
            data-slot="side-panel-footer"
            className={cn(
                'shrink-0 border-t border-line-weak px-container py-container',
                'pb-[max(var(--spacing-container),env(safe-area-inset-bottom))]',
                className,
            )}
            {...props}
        />
    );
}
